import axios from "axios";
import { toast } from "react-toastify";

const api = axios.create({
  baseURL: "https://api.larrydigitals.com/api",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache, no-store, must-revalidate",
    "Pragma": "no-cache",
  },
});

// ── REQUEST DEDUPLICATION ─────────────────────────────────────────────────
const pendingControllers = new Map();

function getRequestKey(config) {
  const path = (config.url || "").split("?")[0];
  return `${config.method}:${path}`;
}

// ── REDIRECT-LOOP GUARD ────────────────────────────────────────────────────
// Once we've decided to force a logout/redirect, block any further 401
// handling until the navigation actually happens. Without this, a burst of
// requests failing at the same moment (e.g. several polls at once) could
// each independently try to redirect, and if something ever prevented the
// navigation from completing, they could stack into a fast reload loop.
let isRedirecting = false;

const PUBLIC_PATHS = ["/login", "/signup", "/", "/forgot-password", "/cookie-policy"];

function isOnPublicPage() {
  return PUBLIC_PATHS.includes(window.location.pathname);
}

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.method === "get" || config.method === "GET") {
      config.params = { ...config.params, _t: Date.now() };
    }

    const key = getRequestKey(config);
    if (pendingControllers.has(key)) {
      pendingControllers.get(key).abort();
    }
    const controller = new AbortController();
    config.signal = controller.signal;
    pendingControllers.set(key, controller);
    config.__requestKey = key;

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    if (response.config?.__requestKey) {
      pendingControllers.delete(response.config.__requestKey);
    }
    return response;
  },
  async (error) => {
    if (error.config?.__requestKey) {
      pendingControllers.delete(error.config.__requestKey);
    }

    if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // If we're already on a public page (login/signup/landing), a 401 here
      // means "not logged in", which is expected — not an error, and
      // definitely not something that should trigger a refresh/redirect cycle.
      if (isOnPublicPage()) {
        return Promise.reject(error);
      }

      // A redirect is already in flight — don't pile on another one.
      if (isRedirecting) {
        return Promise.reject(error);
      }

      try {
        const refreshToken = localStorage.getItem("refreshToken");

        if (!refreshToken) {
          throw new Error("No refresh token found - forcing logout");
        }

        const refreshResponse = await axios.post(
          "https://api.larrydigitals.com/api/user/refresh",
          { refreshToken }
        );

        const { accessToken, refreshToken: newRefreshToken } = refreshResponse.data;

        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", newRefreshToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);

        isRedirecting = true;
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");

        toast.error("Session expired. Please log in again.", {
          position: "top-right",
          autoClose: 5000,
        });

        setTimeout(() => {
          window.location.href = "/login";
        }, 1000);

        return Promise.reject(refreshError);
      }
    }

    let errorMessage = "Something went wrong. Please try again.";

    if (error.response) {
      const { status, data } = error.response;

      if (status === 401) {
        errorMessage = "Session expired. Redirecting to login...";
      } else if (status === 429) {
        errorMessage = "Too many requests. Please wait.";
      } else if (status === 400 || status === 403 || status === 500) {
        errorMessage = data.message || `Error ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error or server unavailable. Check your connection.";
    } else {
      errorMessage = error.message;
    }

    // Don't spam toasts for the expected 401s on public pages
    if (!(error.response?.status === 401 && isOnPublicPage())) {
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });
    }

    console.error("API Error:", {
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
    });

    return Promise.reject(error);
  }
);

export const refreshIfNearExpiry = async () => {
  const token = localStorage.getItem("accessToken");
  if (!token) return;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expiry = payload.exp * 1000;
    const now = Date.now();

    if (expiry - now < 120000) {
      console.log("[Auth] Token near expiry - refreshing proactively");
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) throw new Error("No refresh token");

      const res = await api.post("/user/refresh", { refreshToken });
      const { accessToken, refreshToken: newRefresh } = res.data;

      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", newRefresh);
    }
  } catch (err) {
    console.error("[Auth] Proactive refresh failed:", err);
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    if (!isOnPublicPage()) {
      window.location.href = "/login";
    }
  }
};

export const logout = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  window.location.href = "/login";
  toast.info("Logged out successfully");
};

export default api;